// Import directly from the core implementation to avoid circular deps
import { defineStore } from 'pinia'
import { defaultParams } from '@/utils/constants'

export const useStockStore = defineStore('stock', {
  state: () => ({
    dispatchDay: null,
    deliveryDateFrom: null,
    deliveryDateTo: null,
    cutoff: null,
    leftEyeStock: {},
    rightEyeStock: {},
    lineItemStock: {},
    accessoryStock: {},
  }),

  actions: {
    // Mutation equivalents
    setGuesstimation(guesstimation) {
      this.dispatchDay = guesstimation.dispatchDay
      this.deliveryDateFrom = guesstimation.deliveryDateFrom
      this.deliveryDateTo = guesstimation.deliveryDateTo
      this.cutoff = guesstimation.cutoff
    },

    resetGuesstimation() {
      this.dispatchDay = null
      this.deliveryDateFrom = null
      this.deliveryDateTo = null
      this.cutoff = null
    },

    setLeftEyeStock(stock) {
      this.leftEyeStock = stock
    },

    setRightEyeStock(stock) {
      this.rightEyeStock = stock
    },

    setLineItemStock(stock) {
      this.lineItemStock = stock
    },

    setAccessoryStock(stock) {
      this.accessoryStock = stock
    },

    resetStock() {
      this.leftEyeStock = {}
      this.rightEyeStock = {}
      this.accessoryStock = {}
    },

    resetLeftEyeStock() {
      this.leftEyeStock = {}
    },

    resetRightEyeStock() {
      this.rightEyeStock = {}
    },

    resetAccessoryStock() {
      this.accessoryStock = {}
    },

    resetLineItemStock() {
      this.lineItemStock = {}
    },

    resetStockData() {
      this.resetStock()
      this.resetGuesstimation()
    },

    resetLineItemStockData({ rootStore = null } = {}) {
      if (rootStore) {
        rootStore.removeError('lineItem')
      } else {
        console.warn('No rootStore provided, cannot remove lineItem error')
      }
      this.resetLineItemStock()
    },

    // Action implementations
    async getGuesstimation(options = {}) {
      const { rootStore = null, $axios = null } = options

      // During migration, we need a rootStore that provides prescription data
      if (!rootStore) {
        console.warn('No rootStore provided, cannot get prescription data for guesstimation')
        return
      }

      const prescriptionGetters = rootStore.prescriptionGetters
      const prescriptionState = rootStore.prescriptionState
      const rootState = rootStore.state

      // If we don't have the necessary data, we can't proceed
      if (!prescriptionGetters?.optionsStrings) {
        console.warn('Missing prescription getters')
        return
      }

      // Get axios from nuxt context if not provided
      const axiosInstance = $axios || (process.client && window.$nuxt ? window.$nuxt.$axios : null)

      if (!axiosInstance) {
        console.error('❌ No axios instance available')
        if (rootStore) {
          rootStore.addError({ id: 'guesstimation', error: new Error('No axios instance') })
        }
        return
      }

      const { leftEye: leftEyeString, rightEye: rightEyeString } = prescriptionGetters.optionsStrings
      const { isLeftEyeSelected, isRightEyeSelected, quantity: qty } = prescriptionState
      const { limited: isLeftEyeLimited = false, stock: leftEyeStock = 0 } = this.leftEyeStock
      const { limited: isRightEyeLimited = false, stock: rightEyeStock = 0 } = this.rightEyeStock
      const isLeftEyeValid = isLeftEyeSelected && leftEyeString && !(isLeftEyeLimited && leftEyeStock === 0)
      const isRightEyeValid = isRightEyeSelected && rightEyeString && !(isRightEyeLimited && rightEyeStock === 0)
      const guesstimationOptions = []

      if (isLeftEyeValid && isRightEyeValid) {
        if (leftEyeString === rightEyeString) {
          guesstimationOptions.push({ option: leftEyeString, qty: qty * 2 })
        } else {
          guesstimationOptions.push({ option: leftEyeString, qty })
          guesstimationOptions.push({ option: rightEyeString, qty })
        }
      } else if (isLeftEyeValid && !isRightEyeSelected) {
        guesstimationOptions.push({ option: leftEyeString, qty })
      } else if (isRightEyeValid && !isLeftEyeSelected) {
        guesstimationOptions.push({ option: rightEyeString, qty })
      } else if (rootState?.product?.options?.length === 0) {
        guesstimationOptions.push({ option: rootState.product.id, qty })
      } else {
        this.resetGuesstimation()
        return
      }

      // Use the rootStore for error and loading state management
      rootStore.removeError('guesstimation')
      rootStore.addLoading('guesstimation')

      try {
        const params = { options: JSON.stringify(guesstimationOptions), ...defaultParams }
        const response = await axiosInstance.get('/stock/guesstimation', { params })
        const { data: guesstimation } = response.data
        const deserializedGuesstimation = {
          dispatchDate: guesstimation.estimatedDispatchDay,
          deliveryDateFrom: guesstimation.estimatedDelivery.from,
          deliveryDateTo: guesstimation.estimatedDelivery.to,
          cutoff: guesstimation.cutoff,
        }

        this.setGuesstimation(deserializedGuesstimation)
      } catch (error) {
        rootStore.addError({ id: 'guesstimation', error })
      }

      rootStore.removeLoading('guesstimation')
    },

    async getLeftEyeStock(options = {}) {
      const { rootStore = null, prescriptionStore = null, $axios = null } = options

      // During migration, we need a rootStore that provides prescription data
      if (!rootStore) {
        console.warn('No rootStore provided, cannot get prescription data for left eye stock')
        return
      }
      rootStore.removeError('leftEye')

      const prescriptionGetters = rootStore.prescriptionGetters

      // If we don't have the necessary data, we can't proceed
      if (!prescriptionGetters?.optionsStrings) {
        console.warn('Missing prescription getters')
        return
      }

      const { leftEye: leftEyeId } = prescriptionGetters.optionsStrings

      if (!leftEyeId || leftEyeId === this.leftEyeStock.id) return

      // Get axios from nuxt context if not provided
      const axiosInstance = $axios || (process.client && window.$nuxt ? window.$nuxt.$axios : null)

      if (!axiosInstance) {
        console.error('❌ No axios instance available')
        rootStore.addError({ id: 'leftEye', error: new Error('No axios instance') })
        return
      }

      rootStore.addLoading('leftEye')

      try {
        const params = { options1: leftEyeId, ...defaultParams }
        const response = await axiosInstance.get('/stock/livestock', { params })
        const { data: stock = {} } = response.data

        this.setLeftEyeStock({ ...stock, id: leftEyeId })

        if (prescriptionStore) {
          prescriptionStore.limitQuantity()
        } else if (rootStore) {
          // Use rootStore to dispatch to prescription store
          await rootStore.dispatchToPrescription('limitQuantity', null)
        }
      } catch (error) {
        console.error('❌ Left eye stock error:', error)
        rootStore.addError({ id: 'leftEye', error })
        this.resetLeftEyeStock()
      }

      rootStore.removeLoading('leftEye')
    },

    async getRightEyeStock(options = {}) {
      const { rootStore = null, prescriptionStore = null, $axios = null } = options

      // During migration, we need a rootStore that provides prescription data
      if (!rootStore) {
        console.warn('No rootStore provided, cannot get prescription data for right eye stock')
        return
      }
      rootStore.removeError('rightEye')

      const prescriptionGetters = rootStore.prescriptionGetters

      // If we don't have the necessary data, we can't proceed
      if (!prescriptionGetters?.optionsStrings) {
        console.warn('Missing prescription getters')
        return
      }

      const { rightEye: rightEyeId } = prescriptionGetters.optionsStrings

      if (!rightEyeId || rightEyeId === this.rightEyeStock.id) return

      // Get axios from nuxt context if not provided
      const axiosInstance = $axios || (process.client && window.$nuxt ? window.$nuxt.$axios : null)

      if (!axiosInstance) {
        console.error('❌ No axios instance available')
        rootStore.addError({ id: 'rightEye', error: new Error('No axios instance') })
        return
      }

      rootStore.addLoading('rightEye')

      try {
        const params = { options1: rightEyeId, ...defaultParams }
        const response = await axiosInstance.get('/stock/livestock', { params })
        const { data: stock = {} } = response.data

        this.setRightEyeStock({ ...stock, id: rightEyeId })

        if (prescriptionStore) {
          prescriptionStore.limitQuantity()
        } else if (rootStore) {
          // Use rootStore to dispatch to prescription store
          await rootStore.dispatchToPrescription('limitQuantity', null)
        }
      } catch (error) {
        console.error('❌ Right eye stock error:', error)
        rootStore.addError({ id: 'rightEye', error })
        this.resetRightEyeStock()
      }

      rootStore.removeLoading('rightEye')
    },

    async getLineItemStock(options = {}) {
      const { rootStore = null, $axios = null } = options

      // During migration, we need a rootStore that provides prescription data
      if (!rootStore) {
        console.warn('No rootStore provided, cannot get prescription data for line item stock')
        return
      }
      rootStore.removeError('lineItem')

      const prescriptionGetters = rootStore.prescriptionGetters

      // If we don't have the necessary data, we can't proceed
      if (!prescriptionGetters?.lineItemOptionsString) {
        console.warn('Missing prescription getters')
        return
      }

      const lineItemId = prescriptionGetters.lineItemOptionsString

      if (!lineItemId || lineItemId === this.lineItemStock.id) return

      // Get axios from nuxt context if not provided
      const axiosInstance = $axios || (process.client && window.$nuxt ? window.$nuxt.$axios : null)

      if (!axiosInstance) {
        console.error('❌ No axios instance available')
        rootStore.addError({ id: 'lineItem', error: new Error('No axios instance') })
        return
      }

      rootStore.addLoading('lineItem')

      try {
        const params = { options1: lineItemId, ...defaultParams }
        const response = await axiosInstance.get('/stock/livestock', { params })
        const { data: stock = {} } = response.data

        this.setLineItemStock({ ...stock, id: lineItemId })
      } catch (error) {
        console.error('❌ Line item stock error:', error)
        rootStore.addError({ id: 'lineItem', error })
        this.resetLineItemStock()
      }

      rootStore.removeLoading('lineItem')
    },

    async getAccessoryStock(options = {}) {
      const { rootStore = null, prescriptionStore = null, $axios = null } = options

      // During migration, we need a rootStore that provides product data
      if (!rootStore) {
        console.warn('No rootStore provided, cannot get product data for accessory stock')
        return
      }
      rootStore.removeError('accessory')

      const rootState = rootStore.state

      // If we don't have product data, we can't proceed
      if (!rootState?.product?.id) {
        console.warn('Missing product state')
        return
      }

      // Get axios from nuxt context if not provided
      const axiosInstance = $axios || (process.client && window.$nuxt ? window.$nuxt.$axios : null)

      if (!axiosInstance) {
        console.error('❌ No axios instance available')
        rootStore.addError({ id: 'accessory', error: new Error('No axios instance') })
        return
      }

      rootStore.addLoading('accessory')

      try {
        const params = { options1: rootState.product.id, ...defaultParams }
        const response = await axiosInstance.get('/stock/livestock', { params })
        const { data: stock = {} } = response.data

        this.setAccessoryStock(stock)

        if (prescriptionStore) {
          prescriptionStore.limitQuantity()
        } else if (rootStore) {
          // Use rootStore to dispatch to prescription store
          await rootStore.dispatchToPrescription('limitQuantity', null)
        }
      } catch (error) {
        console.error('❌ Accessory stock error:', error)
        rootStore.addError({ id: 'accessory', error })
        this.resetAccessoryStock()
      }

      rootStore.removeLoading('accessory')
    },

    async getStock(options = {}) {
      const { rootStore = null } = options

      // During migration, we need a rootStore that provides product data
      if (!rootStore) {
        console.warn('No rootStore provided, cannot get product data for stock')
        return
      }

      const rootState = rootStore.state

      // If we don't have product data, we can't proceed
      if (!rootState?.product) {
        console.warn('Missing product state')
        return
      }

      if (rootState.product.options && rootState.product.options.length) {
        await this.getLeftEyeStock(options)
        await this.getRightEyeStock(options)
      } else {
        await this.getAccessoryStock(options)
      }
    },
  },
})
